/**
 * Контракт Recommendations V2 — типы ответа `view=recommendations-v2`.
 *
 * Формы взяты с живого сертифицированного эндпойнта (замер 2026-08-13), а не
 * придуманы: девять правил, конверт `{ rows }`, у каждого правила пятнадцать
 * обязательных полей плюс два условных — `inapplicability` у неприменимых и
 * `top_cargos` у агрегата R8.
 *
 * ЧТО ЗДЕСЬ ВАЖНО ПОНИМАТЬ ПРО ГРАНИЦЫ
 * Фронтенд ничего не пересчитывает. Ни применимость, ни счётчики, ни пороги, ни
 * связи правил не выводятся на клиенте — всё приходит с сервера и только
 * отображается. Поэтому здесь нет ни одной функции, которая принимала бы
 * решение о содержании рекомендации: только описание формы.
 */

/**
 * Единица счёта. Сервер отдаёт `pair` — это строка пары импорт/экспорт витрины
 * v2, а НЕ кейс аналитика и не отправка. Тип оставлен строкой с известным
 * значением: если сервер когда-нибудь введёт другую единицу, подпись в
 * интерфейсе не должна молча остаться прежней.
 */
export type RecommendationCountUnit = 'pair' | (string & {})

/** Приоритет — метаданные правила, а не вычисленный риск кейса. */
export type RecommendationPriority = 'high' | 'medium' | 'low' | (string & {})

/**
 * Одно свидетельство: что именно проверил предикат правила.
 *
 * `metric` — имя поля витрины (`ml`, `as`, `wd`, `db`, `icr`, `al`, `sls`).
 * `matched` — сколько строк удовлетворило этому отдельному условию; это НЕ
 * итоговый счётчик правила, у которого условий может быть несколько.
 */
export interface RecommendationEvidenceV2 {
  metric: string
  operator?: string
  threshold?: number | string
  value?: number | string
  matched?: number
  description: string
}

/**
 * Почему правило неприменимо. Приходит только при `applicable === false`.
 *
 * Единственный известный сегодня код — `THRESHOLD_OUT_OF_OBSERVED_DOMAIN`:
 * порог правила лежит вне диапазона значений поля в текущем наборе данных.
 * Тип оставлен расширяемым, чтобы новый код с сервера не ломал отрисовку.
 */
export interface RecommendationInapplicabilityV2 {
  reason_code: 'THRESHOLD_OUT_OF_OBSERVED_DOMAIN' | (string & {})
  metric: string
  threshold: number | string
  observed_domain: number | string
  description: string
}

/**
 * Связь с другим правилом.
 *
 * `basis` существен: `predicate` — свойство самих условий, верное на любых
 * данных; `snapshot` — измерено на текущем снимке и может измениться.
 */
export interface RecommendationRelationV2 {
  type: 'DISJOINT_WITH' | 'SUBSET_OF' | 'AGGREGATE_OF' | (string & {})
  rule_ids: string[]
  basis: 'predicate' | 'snapshot' | (string & {})
  description: string
}

/** Строка агрегата R8: груз и число строк уровня «высокий» по нему. */
export interface RecommendationTopCargoV2 {
  cargo: string
  high_count: number
  total: number
}

/**
 * Метаданные снимка витрины.
 *
 * `frozen === true` означает, что счётчики пересчитываются на каждый запрос,
 * но данные под ними не обновляются. Интерфейс обязан это показывать, иначе
 * пользователь примет цифры за состояние реального времени.
 */
export interface RecommendationDataSnapshotV2 {
  source?: string
  row_count?: number
  latest_import_date?: string
  latest_export_date?: string
  frozen: boolean
  note?: string
}

/** Рекомендация V2 — ровно то, что отдаёт сервер. */
export interface RecommendationV2 {
  rule_id: string
  title: string
  priority: RecommendationPriority
  reason: string
  applicable: boolean
  case_count: number
  count_unit: RecommendationCountUnit
  evidence: RecommendationEvidenceV2[]
  actions: string[]
  scenario_ids: number[]
  scenario_counts: Record<string, number>
  sample_case_ids: string[]
  relations: RecommendationRelationV2[]
  data_snapshot: RecommendationDataSnapshotV2
  advisory?: string
  /** Только при `applicable === false`. */
  inapplicability?: RecommendationInapplicabilityV2
  /** Только у агрегата R8. */
  top_cargos?: RecommendationTopCargoV2[]
}

/** Конверт ответа `view=recommendations-v2`. */
export interface RecommendationsV2Response {
  rows: RecommendationV2[]
}

/**
 * Проверка формы ответа перед отрисовкой.
 *
 * Нужна не ради типобезопасности — её даёт TypeScript, — а ради решения о
 * переходе на легаси. Сломанный контракт V2 должен приводить к запасному пути,
 * а не к пустой или наполовину отрисованной вкладке.
 *
 * Проверяются только те поля, без которых интерфейс не сможет ничего показать
 * осмысленно. Отсутствие `advisory`, `relations` или `top_cargos` ошибкой не
 * считается: это либо необязательные, либо условные поля.
 *
 * ВАЖНО: пустой `rows` — валидный ответ, а не поломка. Правил может не быть,
 * и это отдельное состояние интерфейса, а не повод уходить на легаси.
 */
export function isRecommendationsV2Response(value: unknown): value is RecommendationsV2Response {
  if (!value || typeof value !== 'object') return false
  const rows = (value as { rows?: unknown }).rows
  if (!Array.isArray(rows)) return false
  return rows.every(isRecommendationV2)
}

/** Минимально достаточная форма одного правила. */
function isRecommendationV2(value: unknown): value is RecommendationV2 {
  if (!value || typeof value !== 'object') return false
  const r = value as Record<string, unknown>
  return (
    typeof r.rule_id === 'string' &&
    r.rule_id.length > 0 &&
    typeof r.title === 'string' &&
    typeof r.reason === 'string' &&
    typeof r.priority === 'string' &&
    typeof r.applicable === 'boolean' &&
    typeof r.case_count === 'number' &&
    Number.isFinite(r.case_count) &&
    typeof r.count_unit === 'string' &&
    Array.isArray(r.evidence) &&
    Array.isArray(r.actions) &&
    Array.isArray(r.scenario_ids) &&
    Array.isArray(r.sample_case_ids) &&
    !!r.scenario_counts &&
    typeof r.scenario_counts === 'object' &&
    !!r.data_snapshot &&
    typeof r.data_snapshot === 'object' &&
    typeof (r.data_snapshot as { frozen?: unknown }).frozen === 'boolean'
  )
}
